/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-program.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent, DialogActions } from '@mui/material';
import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import { useTranslation, useBoolean } from 'hooks';
import { OrganisationProgramFields } from 'queries/generated/fragments.generated';
import { SAVE_ORGANISATION_PROGRAM } from 'queries/org-admin';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import { checkIfNameExist, validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

interface ICustomProgramFields extends OrganisationProgramFields {
    studiesAmount?: number;
}

const EditProgram = ({
    open,
    program,
    programs,
    onClose,
}: {
    open: boolean;
    program: ICustomProgramFields;
    programs: ICustomProgramFields[];
    onClose: Function;
}) => {
    const { t } = useTranslation();
    const [saveProgram] = useMutation(SAVE_ORGANISATION_PROGRAM);

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(null);

    const disabledSave = useBoolean(true);

    useEffect(() => {
        if (open) setName(program?.name ?? name);
    }, [program, open]);

    const close = (action?: string) => {
        if (action !== 'error') {
            setNameError(null);
            setName('');
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const submit = () => {
        const error = validateFields(name, 'name', 70, true);
        const submitData = {
            id: program?.id ?? null,
            name: name.trim(),
            indicationIds: program?.indicationIds ?? [],
            productIds: program?.productIds ?? [],
        };

        if (error.name) {
            setNameError(error.name);
            return;
        }

        if (checkIfNameExist(submitData, programs, 'name')) {
            setNameError(t('admin:programAlreadyExist'));
            return;
        }

        disabledSave.setTrue();
        saveProgram({
            variables: {
                program: {
                    ...submitData,
                },
                reason: '',
            },
            refetchQueries: [
                {
                    query: GET_ORGANISATION_PROGRAM_PRODUCT,
                },
            ],
        })
            .then(() => {
                disabledSave.setFalse();
                close(program ? 'edit' : 'add');
            })
            .catch(() => {
                disabledSave.setFalse();
                close('error');
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth maxWidth="md" open={open} className="custom-width-popup">
            <DialogTitle id="edit-program-title">
                {program ? `${t('admin:editProgram')}: ${program?.recordId || program?.id}` : t('admin:addProgram')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingTop: 10, paddingLeft: 20, paddingRight: 20 }}>
                {program?.studiesAmount ? (
                    <div className="wrap-warning-message-absolute">
                        <WarningForDialog firstNumber={program?.studiesAmount} type="product" />
                    </div>
                ) : null}
                <TextField
                    id="edit-program-name"
                    autoFocus
                    label={`${t('admin:program')}*`}
                    fullWidth
                    value={name}
                    onChange={(event: any) => {
                        disabledSave.setFalse();
                        setName(event.target.value);
                        const error = validateFields(event.target.value, 'name', 70, false);
                        setNameError(error?.name ?? null);
                    }}
                    size="small"
                    placeholder={t('admin:enterProgram')}
                    helperText={
                        <CharactersRemaining
                            id="edit-program-name-characters"
                            length={name.length}
                            max={70}
                            error={nameError}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    error={!!nameError}
                    className="mb"
                />
            </DialogContent>
            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${!program ? 'flex-end' : ''}`}
            >
                <ModalUpdatedInfo item={program} />
                <SaveButton
                    className="edit-cv-update-btn small"
                    id="edit-program-save"
                    text={program ? 'common:update' : 'common:add'}
                    disabled={nameError || disabledSave.value}
                    onClick={submit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditProgram;
